/**
 * Canvas 2D 기반 Surface 구현.
 *
 * 유일한 DOM 의존 모듈이다. cpapp(React Native)용 Skia 어댑터가 생기면
 * 이 파일의 형제로 들어오고, 나머지 코어는 손대지 않는다 (설계문서 §5.4).
 */
import type {
  BlendMode, DrawRect, ImageFormat, Point, Rect,
  Surface, SurfaceFactory, SurfaceImage,
} from './types.js';

const GCO: Record<BlendMode, GlobalCompositeOperation> = {
  srcOver: 'source-over',
  dstIn: 'destination-in',
  dstOut: 'destination-out',
  srcIn: 'source-in',
  copy: 'copy',
};

function trace(ctx: CanvasRenderingContext2D, pts: readonly Point[], close: boolean): void {
  const first = pts[0]!;
  ctx.beginPath();
  ctx.moveTo(first[0], first[1]);
  for (let i = 1; i < pts.length; i++) {
    const p = pts[i]!;
    ctx.lineTo(p[0], p[1]);
  }
  if (close) ctx.closePath();
}

export class Canvas2DSurface implements Surface {
  /** DOM 호스트가 화면에 붙일 때 쓴다. `@nukki/react`가 이걸 읽는다. */
  readonly canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;

  constructor(width: number, height: number, canvas?: HTMLCanvasElement) {
    this.canvas = canvas ?? document.createElement('canvas');
    this.canvas.width = Math.max(1, Math.round(width));
    this.canvas.height = Math.max(1, Math.round(height));
    const ctx = this.canvas.getContext('2d');
    if (!ctx) throw new Error('2D 컨텍스트를 만들 수 없습니다');
    this.ctx = ctx;
  }

  get width(): number { return this.canvas.width; }
  get height(): number { return this.canvas.height; }

  /** 서피스를 새 크기로 되쓴다. 캔버스를 새로 만들지 않는다 (iOS 예산). */
  resize(width: number, height: number): void {
    this.canvas.width = Math.max(1, Math.round(width));
    this.canvas.height = Math.max(1, Math.round(height));
  }

  private mode(blend: BlendMode): CanvasRenderingContext2D {
    const c = this.ctx;
    c.globalCompositeOperation = GCO[blend];
    return c;
  }

  clear(): void {
    this.ctx.globalCompositeOperation = 'source-over';
    this.ctx.clearRect(0, 0, this.width, this.height);
  }

  fillAll(blend: BlendMode = 'copy', color = '#fff'): void {
    const c = this.mode(blend);
    c.fillStyle = color;
    c.fillRect(0, 0, this.width, this.height);
    c.globalCompositeOperation = 'source-over';
  }

  drawImage(img: SurfaceImage, blend: BlendMode = 'srcOver', dst?: DrawRect): void {
    const c = this.mode(blend);
    const src = img as unknown as CanvasImageSource;
    if (dst) c.drawImage(src, dst.x ?? 0, dst.y ?? 0, dst.w, dst.h);
    else c.drawImage(src, 0, 0);
    c.globalCompositeOperation = 'source-over';
  }

  fillPath(points: readonly Point[], blend: BlendMode = 'srcOver'): void {
    if (points.length < 3) return;
    const c = this.mode(blend);
    c.fillStyle = '#fff';
    trace(c, points, true);
    c.fill();
    c.globalCompositeOperation = 'source-over';
  }

  fillRects(rects: readonly Rect[], blend: BlendMode = 'srcOver'): void {
    if (!rects.length) return;
    const c = this.mode(blend);
    c.fillStyle = '#fff';
    c.beginPath();
    for (const r of rects) c.rect(r.x, r.y, r.w, r.h);
    c.fill();                       // 경로 하나로 한 번에 -- 수천 개라도 빠르다
    c.globalCompositeOperation = 'source-over';
  }

  readAlpha(): Uint8Array {
    const { width: w, height: h } = this;
    const d = this.ctx.getImageData(0, 0, w, h).data;
    const out = new Uint8Array(w * h);
    for (let i = 0, p = 3; i < out.length; i++, p += 4) out[i] = d[p]!;
    return out;
  }

  /** ⚠️ 캔버스 자신을 돌려준다. 복사가 아니다. 즉시 쓰고 보관하지 말 것. */
  asImage(): SurfaceImage {
    return this.canvas as unknown as SurfaceImage;
  }

  async encode(format: ImageFormat = 'png', quality?: number): Promise<Blob> {
    const mime = format === 'webp' ? 'image/webp' : 'image/png';
    return await new Promise<Blob>((resolve, reject) => {
      this.canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('인코딩 실패'))),
        mime,
        mime === 'image/webp' ? quality : undefined,
      );
    });
  }

  /** 0×0으로 줄여 즉시 메모리를 반납한다. iOS는 총량 상한이 있어 중요하다. */
  dispose(): void {
    this.canvas.width = 0;
    this.canvas.height = 0;
  }
}

export function canvas2dSurfaces(): SurfaceFactory {
  return {
    create(width, height) {
      return new Canvas2DSurface(width, height);
    },
    async decode(blob) {
      // EXIF 회전을 적용해야 서버 마스크(exif_transpose 적용)와 방향이 맞는다.
      // 옵션 미지원 브라우저에서는 무시되므로 실패 시 기본 호출로 되돌린다.
      try {
        return (await createImageBitmap(blob, { imageOrientation: 'from-image' })) as SurfaceImage;
      } catch {
        return (await createImageBitmap(blob)) as SurfaceImage;
      }
    },
  };
}

/** DOM 호스트용. Canvas2D 서피스에서 실제 캔버스 엘리먼트를 꺼낸다. */
export function toCanvasElement(surface: Surface): HTMLCanvasElement {
  if (surface instanceof Canvas2DSurface) return surface.canvas;
  throw new Error('Canvas2D 서피스가 아닙니다');
}
